import React, { useMemo, useState } from 'react'

import { ICoordinate } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnConditionValue } from '@makyoapp/makyo-common'
import { HoverEffectVariant, HoverIntensity } from '@makyoapp/makyo-tw-ui'

import {
  CoreComponentBrandkit,
  CoreComponentInfos,
  CoreComponentStyle,
  CoreComponentTableData,
} from '@/core/models/app-components/types'
import { CoordinatesCell } from '@/core/models/columns/coordinates'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey } from '@/shared/builder'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { MAX_ROWS_LIMIT } from '@/shared/constants'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { useCompPagination } from '../../hooks/useCompPagination'
import { CompLocationMultiProps } from './comp'

const zoomMapping: Record<string, number> = {
  far: 5,
  medium: 10,
  near: 15,
}

export interface CompLocationWrapperProps {
  infos: CoreComponentInfos
  tableData: CoreComponentTableData
  brandkit: CoreComponentBrandkit
  children: (props: CompLocationMultiProps) => React.ReactNode
}

export const CompLocationMultiWrapperCore = ({
  infos: { component, settings },
  tableData: { dbTable: table },
  brandkit: { mode },
  children,
}: CompLocationWrapperProps) => {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const { simulateAction } = useBuilderActionContextV2()

  // pagination
  const datasetLimit = settings[ComponentSettingKey.MultiLocationDataNumItems]
  const isLimited = datasetLimit?.enabled
  const limit = datasetLimit?.value ?? 0
  const maxPerItems = isLimited ? limit : MAX_ROWS_LIMIT

  const { pagination } = useCompPagination({
    compId: component._id,
    defaultPerPage: maxPerItems,
  })

  const filterSetting = settings[ComponentSettingKey.MultiLocationDataFilter] as ColumnConditionValue | undefined

  const [{ rows, allRowsCount }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    pagination,
    filterSetting,
  })

  const coordinatesCol = getSafely(settings, [ComponentSettingKey.MultiLocationDataAddress], '')
  const title = getSafely(settings, [ComponentSettingKey.MultiLocationDataHeader], '')
  const subtitle = getSafely(settings, [ComponentSettingKey.MultiLocationDataSubHeader], '')
  const src = getSafely(settings, [ComponentSettingKey.MultiLocationDataImage], '')
  const icon = getSafely(settings, [ComponentSettingKey.MultiLocationDataMarker], '')
  const avatar = getSafely(settings, [ComponentSettingKey.MultiLocationDataAvatar], '')
  const customMarker = getSafely(settings, [ComponentSettingKey.MultiLocationDataMarker], '')
  const clickEvents = getSafely(settings, [ComponentSettingKey.MultiLocationActionOnClick], '')
  const infoBubble = getSafely(settings, [ComponentSettingKey.MultiLocationBehaviorInfoBubble], 'default')
  const description = getSafely(settings, [ComponentSettingKey.MultiLocationDataDescription], '')

  const clustered = getSafely(settings, [ComponentSettingKey.MultiLocationDesignCluster], true)
  const zoomControl = getSafely(settings, [ComponentSettingKey.MultiLocationBehaviorZoomControl], true)
  const zoomLevel = getSafely(settings, [ComponentSettingKey.MultiLocationBehaviorZoomLevel], 'far')
  const mapTypeControl = getSafely(settings, [ComponentSettingKey.MultiLocationBehaviorMapTypeControl], true)
  const draggable = getSafely(settings, [ComponentSettingKey.MultiLocationBehaviorDraggable], true)
  const streetViewControl = getSafely(settings, [ComponentSettingKey.MultiLocationBehaviorStreetViewControl], true)
  const fullscreenControl = getSafely(settings, [ComponentSettingKey.MultiLocationBehaviorFullscreenControl], true)
  const width = getSafely(settings, [ComponentSettingKey.MultiLocationDesignWidth], [100])
  const height = getSafely(settings, [ComponentSettingKey.MultiLocationDesignHeight], [900])
  const alignment = getSafely(settings, [ComponentSettingKey.MultiLocationDesignAlignment], 'center')
  const colorVariant = getSafely(settings, [ComponentSettingKey.MultiLocationDesignScheme], 'primary-90')
  const colorMarker = getSafely(settings, [ComponentSettingKey.MultiLocationDesignColorMarker], 'primary-90')
  const margin = settings[ComponentSettingKey.MultiLocationDesignMargin] as SpacingInputValue
  const shape = settings?.[ComponentSettingKey.MultiLocationDesignShape]

  const hoverEffect = settings[ComponentSettingKey.MultiLocationDesignHoverEffect] as HoverEffectVariant
  const hoverIntensity = 'md' as HoverIntensity

  const markerItems = useMemo(() => {
    return rows
      .map((row) => {
        const coordinateCell = row[coordinatesCol] as CoordinatesCell
        const rowIndex = rows.findIndex((r) => r._id === row._id)
        return {
          tableId: table.settings._id,
          rowId: row._id,
          rowIndex,
          latitude: (coordinateCell?.value as ICoordinate)?.Lat,
          longitude: (coordinateCell?.value as ICoordinate)?.Lon,
          title: row[title]?.value as string,
          subtitle: row[subtitle]?.value as string,
          image: row[src]?.value as string,
          icon: row[icon]?.value as string,
          avatar: row[avatar]?.value as string,
          clickEvents: clickEvents as any,
          customMarker: row[customMarker]?.value as string,
          info: infoBubble,
          draggable,
          description: row[description]?.value as string,
          theme: mode,
          shape,
        }
      })
      .filter((row) => !!row.latitude && !!row.longitude)
  }, [
    avatar,
    clickEvents,
    coordinatesCol,
    customMarker,
    description,
    draggable,
    icon,
    infoBubble,
    rows,
    src,
    mode,
    subtitle,
    table.settings._id,
    title,
    shape,
  ])

  const [defaultMarker, ...remainingMarkers] = Array.isArray(markerItems) && markerItems.length > 0 ? markerItems : []

  const mapSettings = {
    draggable,
    mapTypeControl,
    streetViewControl,
    zoomControl,
    fullscreenControl,
    info: infoBubble,
    zoomLevel,
    clustered,
    width: width[0],
    height: height[0],
    alignment,
    shape,
  }

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // if (!mapSettings || !defaultMarker?.latitude || !defaultMarker?.longitude) return null

  if (isLoading) {
    return <div>Loading...</div>
  }

  const locationProps = {
    alignment: mapSettings.alignment,
    zoom: zoomMapping[mapSettings.zoomLevel],
    margin,
    previewWidth: `${mapSettings.width}${!String(mapSettings.width).includes('px') && !String(mapSettings.width).includes('%') ? 'px' : ''}`,
    previewHeight: `${mapSettings.height}px`,
    mapSettings,
    mode,
    defaultMarkerSettings: defaultMarker as any,
    remainingMarkers: remainingMarkers as any[],
    markerItems: markerItems as any[],
    colorVariant,
    colorMarker,
    hoverEffect,
    hoverIntensity,
    simulateAction,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(locationProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(locationProps)
}
